import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentOrg } from '../hooks/useCurrentOrg';
import { useGenerators } from '../hooks/useGenerators';
import { useSubmissions } from '../hooks/useSubmissions';
import { DashboardLayout } from '../components/DashboardLayout';
import { CreateGeneratorModal } from '../components/CreateGeneratorModal';
import { GeneratorCard } from '../components/GeneratorCard';
import { OrgSwitcher } from '../components/OrgSwitcher';
import { Icon, type IconName } from '../components/Icon';
import { formatPrice } from '../lib/pricing';
import { isGeneratorConfigV1 } from '../types/generator-config';
import type { GeneratorConfigV1 } from '../types/generator-config';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function DashboardPage() {
    const { user } = useAuth();
    const { org } = useCurrentOrg();
    const { generators, loading: gensLoading, error: gensError, createGenerator } = useGenerators(org?.id);
    const { submissions, countsByGenerator } = useSubmissions({ organizationId: org?.id });

    const [modalOpen, setModalOpen] = useState(false);

    const firstName = (user?.displayName?.split(/\s+/)[0] ?? user?.email?.split('@')[0] ?? '').trim();

    // Stats
    const stats = useMemo(() => {
        const totalGens = generators.length;
        const publishedGens = generators.filter((g) => g.is_published).length;

        const totalLeads = submissions.length;
        const now = Date.now();
        const lastWeekCutoff = now - WEEK_MS;
        const prevWeekCutoff = now - 2 * WEEK_MS;
        const leadsThisWeek = submissions.filter(
            (s) => new Date(s.created_at).getTime() >= lastWeekCutoff
        ).length;
        const leadsPrevWeek = submissions.filter((s) => {
            const t = new Date(s.created_at).getTime();
            return t >= prevWeekCutoff && t < lastWeekCutoff;
        }).length;

        const newLeads = submissions.filter((s) => s.status === 'new').length;

        const activeLeads = submissions.filter(
            (s) => s.status !== 'lost' && s.status !== 'converted'
        );
        const pipeline = activeLeads.reduce(
            (acc, s) => acc + (s.estimated_total ?? 0),
            0
        );

        const currency = (() => {
            for (const g of generators) {
                if (isGeneratorConfigV1(g.config)) {
                    return (g.config as GeneratorConfigV1).branding.currency;
                }
            }
            return 'EUR';
        })();

        return {
            totalGens,
            publishedGens,
            totalLeads,
            leadsThisWeek,
            leadsPrevWeek,
            newLeads,
            activeLeads: activeLeads.length,
            pipeline,
            currency,
        };
    }, [generators, submissions]);

    const recentLeads = submissions.slice(0, 4);

    const genNameById = useMemo(() => {
        const m: Record<string, string> = {};
        for (const g of generators) m[g.id] = g.name;
        return m;
    }, [generators]);

    return (
        <DashboardLayout>
            <div className="dash-ambient" aria-hidden />
            <main className="dash-main">
                {/* Hero */}
                <section className="dash-hero">
                    <div className="dash-hero-text">
                        <span className="dash-eyebrow">Tableau de bord</span>
                        <h1>
                            Bonjour{firstName ? <>, <span className="dash-hero-name">{firstName}</span></> : null}
                        </h1>
                        <p>
                            Suivez les performances de vos générateurs, gérez vos demandes
                            et affinez vos estimations en temps réel.
                        </p>
                    </div>
                    <div className="dash-hero-side">
                        <OrgSwitcher />
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => setModalOpen(true)}
                        >
                            <Icon name="plus" size={14} strokeWidth={2.4} />
                            Nouveau générateur de devis
                        </button>
                    </div>
                </section>

                {/* Stats */}
                <section className="stats-bento" aria-label="Statistiques clés">
                    <StatCard
                        tone="cyan"
                        icon="grid"
                        label="Générateurs"
                        value={String(stats.totalGens)}
                        footer={
                            stats.totalGens > 0 ? (
                                <StatProgress
                                    value={stats.publishedGens}
                                    max={stats.totalGens}
                                    label={`${stats.publishedGens} publié${stats.publishedGens > 1 ? 's' : ''}`}
                                />
                            ) : (
                                <span className="stat-muted">Aucun pour l'instant</span>
                            )
                        }
                    />

                    <StatCard
                        tone="accent"
                        icon="users"
                        label="Demandes reçues"
                        value={
                            <span className="stat-value-inline">
                                {stats.totalLeads}
                                {stats.newLeads > 0 && (
                                    <span className="stat-pulse" aria-hidden />
                                )}
                            </span>
                        }
                        footer={
                            stats.newLeads > 0 ? (
                                <span className="stat-hot">
                                    {stats.newLeads} nouvelle{stats.newLeads > 1 ? 's' : ''} à traiter
                                </span>
                            ) : (
                                <span className="stat-muted">Aucune nouveauté</span>
                            )
                        }
                    />

                    <StatCard
                        tone="green"
                        icon="trending-up"
                        label="Cette semaine"
                        value={String(stats.leadsThisWeek)}
                        footer={<TrendIndicator current={stats.leadsThisWeek} previous={stats.leadsPrevWeek} />}
                    />

                    <StatCard
                        tone="amber"
                        icon="price"
                        label="Pipeline estimé"
                        value={formatPrice(stats.pipeline, stats.currency)}
                        footer={
                            stats.activeLeads > 0 ? (
                                <span className="stat-muted">
                                    {stats.activeLeads} lead{stats.activeLeads > 1 ? 's' : ''} actif{stats.activeLeads > 1 ? 's' : ''}
                                </span>
                            ) : (
                                <span className="stat-muted">Aucun lead actif</span>
                            )
                        }
                    />
                </section>

                {gensError && (
                    <div className="auth-error" role="alert" style={{ marginBottom: 24 }}>
                        {gensError}
                    </div>
                )}

                {/* Grille principale */}
                <div className="dash-grid">
                    {/* Générateurs */}
                    <section className="panel panel-gens">
                        <header className="panel-head">
                            <div className="panel-head-title">
                                <span className="panel-head-rail" aria-hidden />
                                <div>
                                    <h2>Vos générateurs de devis</h2>
                                    <p>
                                        {stats.totalGens === 0
                                            ? 'Créez votre premier générateur pour commencer.'
                                            : `${stats.totalGens} générateur${stats.totalGens > 1 ? 's' : ''} — ${stats.publishedGens} publié${stats.publishedGens > 1 ? 's' : ''}`}
                                    </p>
                                </div>
                            </div>
                            {stats.totalGens > 0 && (
                                <button
                                    type="button"
                                    className="btn-ghost"
                                    onClick={() => setModalOpen(true)}
                                >
                                    <Icon name="plus" size={13} strokeWidth={2.4} />
                                    Ajouter
                                </button>
                            )}
                        </header>

                        {gensLoading ? (
                            <div className="dash-loading">
                                <div className="spinner" aria-hidden />
                                <span>Chargement…</span>
                            </div>
                        ) : generators.length === 0 ? (
                            <div className="panel-empty">
                                <div className="panel-empty-icon" aria-hidden>
                                    <Icon name="grid" size={28} />
                                </div>
                                <h3>Aucun générateur pour l'instant</h3>
                                <p>
                                    Créez votre premier générateur de devis. Personnalisez-le,
                                    publiez-le, puis commencez à recevoir des demandes qualifiées.
                                </p>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => setModalOpen(true)}
                                >
                                    <Icon name="plus" size={14} strokeWidth={2.4} />
                                    Créer un générateur
                                </button>
                            </div>
                        ) : (
                            <div className="gen-grid">
                                {generators.map((g) => (
                                    <GeneratorCard
                                        key={g.id}
                                        generator={g}
                                        orgSlug={org?.slug ?? null}
                                        leadsCount={countsByGenerator[g.id] ?? 0}
                                    />
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Activité récente — feed */}
                    <aside className="panel panel-feed">
                        <header className="panel-head">
                            <div className="panel-head-title">
                                <span className="panel-head-rail panel-head-rail-accent" aria-hidden />
                                <div>
                                    <h2>Activité récente</h2>
                                    <p>Les dernières demandes reçues.</p>
                                </div>
                            </div>
                            {submissions.length > 0 && (
                                <Link to="/leads" className="panel-head-link">
                                    Tout voir
                                    <Icon name="chevron-right" size={13} />
                                </Link>
                            )}
                        </header>

                        {recentLeads.length === 0 ? (
                            <div className="feed-empty">
                                <span className="feed-empty-icon" aria-hidden>
                                    <Icon name="mail" size={22} />
                                </span>
                                <p>Aucune demande pour l'instant.</p>
                                <small>Publiez un générateur pour commencer à recevoir des leads.</small>
                            </div>
                        ) : (
                            <ol className="feed">
                                {recentLeads.map((s, idx) => (
                                    <li
                                        key={s.id}
                                        className={`feed-item${idx === recentLeads.length - 1 ? ' is-last' : ''}`}
                                    >
                                        <span className="feed-marker" aria-hidden>
                                            <span
                                                className={`feed-dot${s.status === 'new' ? ' is-new' : ''}`}
                                            />
                                        </span>
                                        <Link to="/leads" className="feed-link">
                                            <span className="feed-time">{formatRelative(s.created_at)}</span>
                                            <span className="feed-title">
                                                {s.contact_name || s.contact_email || 'Contact anonyme'}
                                            </span>
                                            <span className="feed-meta">
                                                <span className="feed-gen">
                                                    {genNameById[s.generator_id] ?? '—'}
                                                </span>
                                                {s.estimated_total != null && (
                                                    <>
                                                        <span className="feed-sep" aria-hidden>·</span>
                                                        <span className="feed-amount">
                                                            {formatPrice(s.estimated_total, stats.currency)}
                                                        </span>
                                                    </>
                                                )}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </aside>
                </div>
            </main>

            <CreateGeneratorModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={async (payload) => {
                    await createGenerator(payload);
                }}
            />
        </DashboardLayout>
    );
}

/* ================================================================
   Stat card — éditorial, avec rail coloré + bulle de gradient
   ================================================================ */

type StatTone = 'cyan' | 'accent' | 'green' | 'amber';

interface StatCardProps {
    icon: IconName;
    label: string;
    value: ReactNode;
    footer?: ReactNode;
    tone?: StatTone;
}

function StatCard({ icon, label, value, footer, tone = 'accent' }: StatCardProps) {
    return (
        <article className={`stat stat--${tone}`}>
            <div className="stat-blob" aria-hidden />
            <header className="stat-head">
                <span className="stat-icon" aria-hidden>
                    <Icon name={icon} size={15} />
                </span>
                <span className="stat-label">{label}</span>
            </header>
            <div className="stat-value">{value}</div>
            {footer && <div className="stat-footer">{footer}</div>}
        </article>
    );
}

/* ---- Mini composants pour les footers de stat ---- */

function StatProgress({
    value,
    max,
    label,
}: {
    value: number;
    max: number;
    label: string;
}) {
    const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
    return (
        <div className="stat-progress" title={`${value} / ${max}`}>
            <div
                className="stat-progress-bar"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <span style={{ width: `${pct}%` }} />
            </div>
            <span className="stat-progress-label">{label}</span>
        </div>
    );
}

function TrendIndicator({ current, previous }: { current: number; previous: number }) {
    if (current === 0 && previous === 0) {
        return <span className="stat-muted">Aucune demande cette semaine</span>;
    }
    if (previous === 0 && current > 0) {
        return (
            <span className="stat-trend is-up">
                <Icon name="trending-up" size={12} />
                Nouveau flux cette semaine
            </span>
        );
    }
    if (current === previous) {
        return <span className="stat-muted">Stable vs semaine précédente</span>;
    }
    const diff = current - previous;
    const pct = Math.round((Math.abs(diff) / Math.max(previous, 1)) * 100);
    const up = diff > 0;
    return (
        <span className={`stat-trend ${up ? 'is-up' : 'is-down'}`}>
            <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
                style={up ? undefined : { transform: 'scaleY(-1)' }}
            >
                <polyline points="5 12 12 5 19 12" />
                <line x1="12" y1="5" x2="12" y2="19" />
            </svg>
            {up ? '+' : '−'}{pct}% vs semaine précédente
        </span>
    );
}

function formatRelative(iso: string): string {
    const now = Date.now();
    const then = new Date(iso).getTime();
    const diff = Math.max(0, now - then);
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'à l\'instant';
    if (minutes < 60) return `il y a ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `il y a ${hours} h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `il y a ${days} j`;
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}
